import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * A single ⋮ button that reveals row actions (Edit/Restart/Delete, Open/Delete, etc.) on
 * click. Two iterations before this one, both reported live as still wrong:
 *
 *   1. A plain `position: absolute` dropdown got clipped/overlapped by .table-card's
 *      overflow-x: auto (which forces the browser to compute overflow-y: hidden too) —
 *      landed on top of the NEXT row's own ⋮ button, looking like two stacked kebabs.
 *   2. Portaled to document.body with position:fixed to escape the clipping — fixed the
 *      clip, but a floating overlay on top of the next row(s) was still confusing and
 *      explicitly NOT what was wanted: "it should [not] overlap".
 *
 * This version inserts a genuine extra <tr> directly after the row's own <tr>, in normal
 * table flow — not a floating overlay at all. Clicking ⋮ pushes every row below it DOWN
 * the page, the same way an accordion expands, so nothing is ever drawn on top of anything.
 * Achieved by maintaining a one-row-tall placeholder <tr> (inserted into the DOM directly,
 * not via React) right after the trigger row, and portaling the menu's content into it —
 * a plain sibling <tr> can't be returned from here since this component always renders
 * inside a single <td>, and React has no "insert a sibling to my own parent" primitive.
 */
export default function ActionsMenu({ children }) {
  const [open, setOpen] = useState(false);
  // The placeholder <td> lives in state (not a plain ref) so creating it triggers the
  // re-render that lets the portal below actually mount into it on the SAME open click —
  // a ref alone would only become visible on open's NEXT render, one click too late.
  const [placeholderTd, setPlaceholderTd] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = () => setOpen(o => !o);

  // Create/destroy the placeholder <tr> as a sibling of the trigger row, in sync with
  // `open` — this is what actually pushes later rows down, since it is a real table row
  // taking real layout space, not an overlay.
  useEffect(() => {
    const row = btnRef.current?.closest('tr');
    if (!row || !open) return;

    const colCount = row.children.length;
    const tr = document.createElement('tr');
    const td = document.createElement('td');
    td.colSpan = colCount;
    td.style.padding = '0';
    td.style.border = 'none';
    tr.appendChild(td);
    row.after(tr);
    setPlaceholderTd(td);

    return () => {
      tr.remove();
      setPlaceholderTd(null);
    };
  }, [open]);

  useEffect(() => {
    const row = btnRef.current?.closest('tr');
    // row-info is this table style's OWN existing class for a highlighted row
    // (table.ds tbody tr.row-info td { background: ... } in design-system.css) — setting
    // background directly on <tr> does nothing visible, since table.ds tbody td paints
    // its own opaque background on top of whatever color the <tr> has.
    if (row) row.classList.toggle('row-info', open);
    return () => { if (row) row.classList.remove('row-info'); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-secondary"
        style={{ padding: '3px 10px', fontSize: 14, lineHeight: 1 }}
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        title="Actions"
        aria-label="Actions"
      >
        ⋮
      </button>
      {open && placeholderTd && createPortal(
        <div
          ref={menuRef}
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 16px',
            borderTop: '1px solid var(--border)', background: 'var(--surface-1)',
          }}
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
        >
          {children}
        </div>,
        placeholderTd
      )}
    </>
  );
}

// Plain text links, same look the original vertical dropdown's menu items had — no
// border/box around each one, just the item's own color (red for Delete, via the
// color override each caller already passes) doing the visual work.
export const MENU_ITEM = {
  padding: 0, fontSize: 13, cursor: 'pointer',
  border: 'none', background: 'none', color: 'var(--ink-2)',
};
